import React, { useState, useEffect } from 'react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { format, addMonths, isBefore, startOfDay } from 'date-fns';
import { CalendarBlank, Clock, Users, CaretLeft, CaretRight, X } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CalendarBookingWidget({ listing, user, token, onBook }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [guestCount, setGuestCount] = useState(1);
  const [message, setMessage] = useState('');
  const [availability, setAvailability] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [submitting, setSubmitting] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [showLiabilityWaiver, setShowLiabilityWaiver] = useState(false);

  const slotDuration = listing?.slot_duration || 60;

  useEffect(() => {
    loadAvailability();
  }, [listing?.id, currentMonth]);

  const loadAvailability = async () => {
    if (!listing?.id) return;
    
    setLoading(true);
    try {
      const monthStr = format(currentMonth, 'yyyy-MM');
      const response = await fetch(
        `${API_URL}/api/listings/${listing.id}/availability?month=${monthStr}`
      );
      if (response.ok) {
        const data = await response.json();
        setAvailability(data.availability || []);
        setBlockedDates(data.blocked_dates || []);
      }
    } catch (error) {
      console.error('Failed to load availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateAvailability = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availability.find(a => a.date === dateStr);
  };

  const isDateAvailable = (date) => {
    if (isBefore(startOfDay(date), startOfDay(new Date()))) return false;
    const dateStr = format(date, 'yyyy-MM-dd');
    if (blockedDates.includes(dateStr)) return false;
    const avail = getDateAvailability(date);
    return avail && avail.available_slots && avail.available_slots.length > 0;
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlots([]);
  };

  const toggleSlot = (slot) => {
    if (selectedSlots.includes(slot)) {
      // Remove slot and all slots after it
      const index = selectedSlots.indexOf(slot);
      setSelectedSlots(selectedSlots.slice(0, index));
    } else {
      // Check if slot is consecutive with last selected
      if (selectedSlots.length === 0) {
        setSelectedSlots([slot]);
      } else {
        const lastSlot = selectedSlots[selectedSlots.length - 1];
        const lastHour = parseInt(lastSlot.split(':')[0]);
        const lastMin = parseInt(lastSlot.split(':')[1]);
        const slotHour = parseInt(slot.split(':')[0]);
        const slotMin = parseInt(slot.split(':')[1]);
        
        const lastMinutes = lastHour * 60 + lastMin;
        const slotMinutes = slotHour * 60 + slotMin;
        
        if (slotMinutes === lastMinutes + slotDuration) {
          setSelectedSlots([...selectedSlots, slot]);
        } else {
          // Start new selection
          setSelectedSlots([slot]);
        }
      }
    }
  };

  const calculateEndTime = () => {
    if (selectedSlots.length === 0) return '';
    const lastSlot = selectedSlots[selectedSlots.length - 1];
    const [hour, min] = lastSlot.split(':').map(Number);
    const endMinutes = hour * 60 + min + slotDuration;
    const endHour = Math.floor(endMinutes / 60);
    const endMin = endMinutes % 60;
    return `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
  };

  const calculateTotalPrice = () => {
    const hours = (selectedSlots.length * slotDuration) / 60;
    return (hours * listing.price_per_hour).toFixed(2);
  };

  // Calculate fee breakdown for display
  const calculateFeeBreakdown = () => {
    const listingPrice = parseFloat(calculateTotalPrice());
    // Fee tiers: $0-20: $2, $20-30: $3, $30-40: $4, Above $40: 10%
    let guestServiceFee;
    if (listingPrice < 20) {
      guestServiceFee = 2;
    } else if (listingPrice < 30) {
      guestServiceFee = 3;
    } else if (listingPrice < 40) {
      guestServiceFee = 4;
    } else {
      guestServiceFee = listingPrice * 0.10;
    }
    return {
      listingPrice: listingPrice,
      guestServiceFee: Math.round(guestServiceFee * 100) / 100,
      guestTotal: Math.round((listingPrice + guestServiceFee) * 100) / 100
    };
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error('Please sign in to book');
      return;
    }

    if (!selectedDate || selectedSlots.length === 0) {
      toast.error('Please select a date and time slots');
      return;
    }

    setSubmitting(true);
    try {
      const bookingData = {
        listing_id: listing.id,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        start_time: selectedSlots[0],
        end_time: calculateEndTime(),
        guests_count: guestCount,
        message: message || null,
      };

      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(bookingData),
      });

      if (response.ok) {
        toast.success('Booking request sent! Wait for host approval.');
        if (onBook) onBook();
        // Reset form
        setSelectedDate(null);
        setSelectedSlots([]);
        setMessage('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create booking');
      }
    } catch (error) {
      toast.error('Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const dayAvailability = selectedDate ? getDateAvailability(selectedDate) : null;

  return (
    <div className="bg-[#121212] border border-white/10 p-6">
      <h3 className="text-xl font-serif text-white mb-6">Book This Space</h3>

      {/* Calendar */}
      <div className="mb-6">
        {loading ? (
          <div className="h-64 flex items-center justify-center text-zinc-500">
            Loading availability...
          </div>
        ) : availability.length === 0 ? (
          <div className="bg-[#0A0A0A] border border-white/10 p-6 text-center">
            <CalendarBlank size={48} className="mx-auto text-zinc-600 mb-3" weight="duotone" />
            <p className="text-white font-medium mb-2">No availability set</p>
            <p className="text-zinc-500 text-sm">The host hasn't set up their availability calendar yet. Try messaging the host to inquire about available times.</p>
          </div>
        ) : (
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            month={currentMonth}
            onMonthChange={setCurrentMonth}
            disabled={(date) => !isDateAvailable(date)}
            className="w-full rounded-md border border-white/10 bg-[#0A0A0A] p-3"
            classNames={{
              months: "flex flex-col",
              month: "space-y-4 w-full",
              caption: "flex justify-center pt-1 relative items-center mb-4",
              caption_label: "text-sm font-medium text-white",
              nav: "space-x-1 flex items-center",
              nav_button: "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 border border-white/20 rounded flex items-center justify-center text-white",
              nav_button_previous: "absolute left-1",
              nav_button_next: "absolute right-1",
              table: "w-full border-collapse",
              head_row: "flex w-full",
              head_cell: "text-zinc-500 rounded-md w-full font-normal text-[0.8rem] py-2",
              row: "flex w-full mt-1",
              cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 w-full",
              day: "h-9 w-full p-0 font-normal text-white hover:bg-white/10 rounded-md flex items-center justify-center cursor-pointer",
              day_selected: "bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-md",
              day_today: "bg-white/10 text-white",
              day_outside: "text-zinc-700 opacity-50",
              day_disabled: "text-zinc-700 opacity-30 cursor-not-allowed hover:bg-transparent",
              day_hidden: "invisible",
            }}
          />
        )}
        {availability.length > 0 && (
          <div className="flex items-center gap-4 mt-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-[#D4AF37] rounded"></div>
              <span className="text-zinc-400">Selected</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-white/10 rounded"></div>
              <span className="text-zinc-400">Available</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-zinc-800 rounded"></div>
              <span className="text-zinc-400">Unavailable</span>
            </div>
          </div>
        )}
      </div>

      {/* Time Slots */}
      {selectedDate && dayAvailability && (
        <div className="mb-6">
          <Label className="text-white mb-3 block flex items-center gap-2">
            <Clock size={18} className="text-[#D4AF37]" />
            Select Time ({slotDuration} min slots)
          </Label>
          <div className="grid grid-cols-4 gap-2">
            {dayAvailability.available_slots.map((slot) => (
              <button
                key={slot}
                onClick={() => toggleSlot(slot)}
                className={`py-2 px-3 text-sm rounded border transition-all ${
                  selectedSlots.includes(slot)
                    ? 'bg-[#D4AF37] border-[#D4AF37] text-black'
                    : 'border-white/20 text-zinc-400 hover:border-white/40'
                }`}
              >
                {slot}
              </button>
            ))}
          </div>
          {dayAvailability.available_slots.length === 0 && (
            <p className="text-zinc-500 text-sm">No available time slots for this date</p>
          )}
        </div>
      )}

      {/* Selected Time Summary */}
      {selectedSlots.length > 0 && (
        <div className="bg-[#0A0A0A] border border-white/10 p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-zinc-400">Date</span>
            <span className="text-white">{format(selectedDate, 'MMM d, yyyy')}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-zinc-400">Time</span>
            <span className="text-white">{selectedSlots[0]} - {calculateEndTime()}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-zinc-400">Duration</span>
            <span className="text-white">{(selectedSlots.length * slotDuration) / 60} hour(s)</span>
          </div>
          <div className="border-t border-white/10 pt-3 mt-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-zinc-400">Space rental</span>
              <span className="text-white">${calculateFeeBreakdown().listingPrice.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-zinc-400">Service fee</span>
              <span className="text-zinc-300">${calculateFeeBreakdown().guestServiceFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center border-t border-white/10 pt-2 mt-2">
              <span className="text-white font-semibold">Total</span>
              <span className="text-[#D4AF37] font-bold text-xl">${calculateFeeBreakdown().guestTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Guest Count */}
      <div className="mb-4">
        <Label className="text-white mb-2 block flex items-center gap-2">
          <Users size={18} className="text-[#D4AF37]" />
          Number of Guests
        </Label>
        <select
          value={guestCount}
          onChange={(e) => setGuestCount(parseInt(e.target.value))}
          className="w-full bg-[#0A0A0A] border border-white/20 text-white py-2 px-3 rounded"
        >
          {Array.from({ length: listing?.max_guests || 10 }, (_, i) => (
            <option key={i + 1} value={i + 1}>{i + 1} guest{i > 0 ? 's' : ''}</option>
          ))}
        </select>
      </div>

      {/* Message to Host */}
      <div className="mb-4">
        <Label className="text-white mb-2 block">Message to Host (optional)</Label>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Introduce yourself or ask a question..."
          className="bg-[#0A0A0A] border-white/20 text-white"
          rows={3}
        />
      </div>

      {/* Cancellation Policy Acknowledgement */}
      <div className="mb-6 bg-[#0a0a0a] border border-white/10 p-4 rounded">
        <p className="text-zinc-400 text-xs mb-3">
          <span className="text-white font-semibold">Cancellation Policy:</span> More than 48 hrs before booking start = 100% refund of host amount. 24-48 hrs = 50% refund. Within 24 hrs = No refund. Service fees are non-refundable.
        </p>
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={policyAccepted}
            onChange={(e) => setPolicyAccepted(e.target.checked)}
            className="w-4 h-4 rounded border-white/10 mt-0.5 cursor-pointer"
          />
          <span className="text-zinc-400 text-xs">
            I acknowledge and agree to the 100Nook cancellation and refund policy and the{' '}
            <button
              type="button"
              onClick={() => setShowLiabilityWaiver(true)}
              className="text-[#D4AF37] hover:underline font-semibold"
            >
              Guest Liability Waiver
            </button>
            .
          </span>
        </div>
      </div>

      {/* Guest Liability Waiver Modal */}
      {showLiabilityWaiver && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#121212] border border-white/10 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-xl font-semibold text-white">Guest Liability Waiver</h2>
              <button
                onClick={() => setShowLiabilityWaiver(false)}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              <p className="text-zinc-400 text-sm mb-6">Last Updated: April 2026</p>
              
              <div className="space-y-6 text-zinc-300">
                <section>
                  <h3 className="text-lg font-semibold text-white mb-3">1. Assumption of Risk</h3>
                  <p className="text-sm mb-3">
                    By booking through 100Nook, Guests acknowledge that use of private spaces may involve inherent risks including but not limited to:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-zinc-400 text-sm">
                    <li>Physical injury</li>
                    <li>Property damage</li>
                    <li>Equipment malfunction</li>
                    <li>Weather-related hazards</li>
                    <li>Slip and fall incidents</li>
                  </ul>
                  <p className="text-sm mt-3 text-zinc-400">
                    Guests voluntarily assume all risks associated with the use of any space booked through 100Nook.
                  </p>
                </section>

                <hr className="border-white/10" />

                <section>
                  <h3 className="text-lg font-semibold text-white mb-3">2. Independent Host Disclaimer</h3>
                  <p className="text-sm mb-3">Guests understand that:</p>
                  <ul className="list-disc list-inside space-y-1 text-zinc-400 text-sm">
                    <li>Hosts are independent parties</li>
                    <li>100Nook does not inspect properties</li>
                    <li>100Nook does not supervise activities</li>
                  </ul>
                  <p className="text-sm mt-3 text-zinc-400">
                    Guests agree that 100Nook is not responsible for any injuries or damages.
                  </p>
                </section>

                <hr className="border-white/10" />

                <section>
                  <h3 className="text-lg font-semibold text-white mb-3">3. Guest Responsibility</h3>
                  <p className="text-sm mb-3">Guests agree to:</p>
                  <ul className="list-disc list-inside space-y-1 text-zinc-400 text-sm">
                    <li>Follow host instructions</li>
                    <li>Follow posted rules</li>
                    <li>Use equipment responsibly</li>
                    <li>Supervise minors</li>
                  </ul>
                  <p className="text-sm mt-3 text-zinc-400">
                    Guests are responsible for damages caused during bookings.
                  </p>
                </section>

                <hr className="border-white/10" />

                <section>
                  <h3 className="text-lg font-semibold text-white mb-3">4. Release of Liability</h3>
                  <p className="text-sm mb-3">Guests release:</p>
                  <p className="text-zinc-400 text-sm mb-3">
                    100Nook and its affiliates, subsidiaries, parent entities, successors, assigns, officers, directors, employees, contractors, and agents
                  </p>
                  <p className="text-sm mb-3">From any claims including:</p>
                  <ul className="list-disc list-inside space-y-1 text-zinc-400 text-sm">
                    <li>Injury</li>
                    <li>Property damage</li>
                    <li>Loss</li>
                    <li>Disputes</li>
                  </ul>
                </section>
              </div>

              <div className="mt-6 pt-4 border-t border-white/10">
                <Button
                  onClick={() => setShowLiabilityWaiver(false)}
                  className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Book Button */}
      <Button
        onClick={handleSubmit}
        disabled={!selectedDate || selectedSlots.length === 0 || submitting || !user || !policyAccepted}
        className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold py-4 disabled:opacity-50"
      >
        {submitting ? 'Sending Request...' : user ? 'Request to Book' : 'Sign in to Book'}
      </Button>

      <p className="text-zinc-500 text-xs text-center mt-3">
        You won't be charged until the host approves your request
      </p>
    </div>
  );
}
